import {
  DateFilter,
  LongExtraLongSkeletonCell,
  ShortenedSpan,
  ShortMediumSkeletonCell,
  ShortSkeletonCell,
} from '@energybox/react-ui-library/dist/components';
import {
  Columns,
  TableGroupHeader,
} from '@energybox/react-ui-library/dist/components/Table';
import {
  FilterOption,
  OpacityIndex,
  SortDirection,
} from '@energybox/react-ui-library/dist/types';
import {
  genericTableSort,
  isNumber,
  SORT_IGNORED_VALUES,
} from '@energybox/react-ui-library/dist/utils';
import { useEffect, useState } from 'react';
import SeeAllPage from '../../../../components/views/SeeAllPage';
import { useOrganizationCO2Report } from '../../../../hooks/useAnalytics';
import { useSearchFilter } from '../../../../hooks/useFilters';
import * as Routes from '../../../../routes';
import * as R from 'ramda';
import { mapValues } from '../../../../util';
import { Link } from 'react-router-dom';
import Button from '../../../../components/ui/Button';
import { Line, LineChart, ResponsiveContainer, Tooltip } from 'recharts';
import { endOfYear, startOfYear } from 'date-fns';
import useAppLocale from '../../../../hooks/useAppLocale';
import { useDispatch, useSelector } from 'react-redux';
import {
  startOrganizationCO2Report,
  startOrganizationEnergySummary,
  startOrganizationEUIReport,
} from '../../../../actions/analytics';
import { useOrganizationId } from '../../../../hooks/useCurrentUser';
import SiteFilter from '../../../../components/Filters/SiteFilter';
import useSiteFilter from '../../../../hooks/useSiteFilter';
import { formatNumber } from '@energybox/react-ui-library/dist/utils/number';
import styles from './AnnualCO2Emission.module.css';
import { DA_REPORT_AVAILABLE_DATE } from '../../constants';
import usePaginationFilter from '../../../../hooks/usePaginationFilter';
import { ApplicationState } from '../../../../reducers';

const KWH_PER_M2_TARGET = 500;

const AnnualCO2Emission: React.FC = () => {
  const dispatch = useDispatch();
  const locale = useAppLocale();
  const orgId = useOrganizationId();
  const { selectedSiteFilters } = useSiteFilter();
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = month >= 2 ? now.getFullYear() : now.getFullYear() - 1;
  const [selectedYear, setSelectedYear] = useState<number>(year);
  const [selectedFilterOption, setSelectedFilterOption] =
    useState<FilterOption>({
      title: String(selectedYear),
      fromDate: startOfYear(now),
      toDate: endOfYear(now),
    });
  const disableAfterOption =
    now.getMonth() > 0
      ? now.getFullYear() + 1 + '-' + now.getMonth()
      : now.getFullYear() - 1 + '-' + now.getMonth();

  const co2Report = useOrganizationCO2Report(selectedYear);
  const { org_co2_total = 0, site_dict, repo_year } = co2Report.data || {};
  const { isLoading } = co2Report;

  /* SITE FILTER BASED ON USER FOR EACH ORG */
  const sites = useSelector((state: ApplicationState) => state.sites.sites);
  const siteIdsStr = sites?.map((site) => site.id).join(',') || '';

  const updateYearParam = (e) => {
    setSelectedYear(e);
    const now = new Date();
    const month = e === now.getFullYear() ? now.getMonth() + 1 : 12;
    setSelectedFilterOption({
      title: e,
      fromDate: startOfYear(new Date(`${e}-${month}-01`)),
      toDate: endOfYear(new Date(`${e}-${month}-01`)),
    });
    dispatch(
      startOrganizationEUIReport(
        orgId || 0,
        `${e}-${month}-01`,
        KWH_PER_M2_TARGET,
        siteIdsStr
      )
    );
    dispatch(
      startOrganizationCO2Report(orgId || 0, `${e}-${month}-01`, siteIdsStr)
    );
    dispatch(
      startOrganizationEnergySummary(orgId || 0, `${e}-${month}-01`, siteIdsStr)
    );
  };

  const updateSelectedFilterOption = (e: FilterOption) => {
    updateYearParam(e.fromDate.getFullYear());
  };

  useEffect(() => {
    if (repo_year && repo_year !== selectedYear) {
      setSelectedYear(selectedYear);
      const now = new Date();
      const month = repo_year === now.getFullYear() ? now.getMonth() + 1 : 12;
      setSelectedFilterOption({
        title: selectedYear.toString(),
        fromDate: startOfYear(new Date(`${selectedYear}-${month}-01`)),
        toDate: endOfYear(new Date(`${selectedYear}-${month}-01`)),
      });
    }
  }, [selectedYear]);

  useEffect(() => {
    if (repo_year && repo_year !== selectedYear) {
      setSelectedYear(repo_year);
      const now = new Date();
      const month = repo_year === now.getFullYear() ? now.getMonth() + 1 : 12;
      setSelectedFilterOption({
        title: repo_year.toString(),
        fromDate: startOfYear(new Date(`${repo_year}-${month}-01`)),
        toDate: endOfYear(new Date(`${repo_year}-${month}-01`)),
      });
    }
  }, []);

  const MONTH_SHORTEN = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const columns: Columns<typeof site_dict>[] = [
    {
      header: 'Site Name',
      width: '20%',
      cellContent: ({ site_title, siteId }) => (
        <div>
          <Link to={`/sites/${siteId}/dashboard`}>
            <ShortenedSpan
              content={site_title}
              maxStringLength={45}
              arrowDirection={'bottom'}
            />
          </Link>
        </div>
      ),
      skeletonCellContent: (rowIndex: OpacityIndex) => (
        <LongExtraLongSkeletonCell opacityIndex={rowIndex} />
      ),
      comparator: (a, b, sortDirection) => {
        return genericTableSort(a, b, sortDirection, SORT_IGNORED_VALUES, [
          'site_title',
        ]);
      },
    },
    {
      header: 'CO₂ Emissions (kg)',
      width: '10%',
      align: 'right',
      rightAlignContent: true,
      defaultSortDirection: SortDirection.DESC,
      cellContent: ({ site_total }) => (
        <div>{formatNumber(site_total || 0, 1)}</div>
      ),
      skeletonCellContent: (rowIndex: OpacityIndex) => (
        <ShortMediumSkeletonCell opacityIndex={rowIndex} />
      ),
      comparator: (a, b, sortDirection) => {
        return genericTableSort(a, b, sortDirection, SORT_IGNORED_VALUES, [
          'site_total',
        ]);
      },
    },
    {
      header: 'CO₂ Factor (CO₂e)',
      width: '10%',
      align: 'right',
      rightAlignContent: true,
      defaultSortDirection: SortDirection.DESC,
      cellContent: ({ co2_factor }) => <div>{formatNumber(co2_factor, 1)}</div>,
      skeletonCellContent: (rowIndex: OpacityIndex) => (
        <ShortMediumSkeletonCell opacityIndex={rowIndex} />
      ),
      comparator: (a, b, sortDirection) => {
        return genericTableSort(a, b, sortDirection, SORT_IGNORED_VALUES, [
          'co2_factor',
        ]);
      },
    },
    {
      header: 'Trend',
      width: '10%',
      align: 'center',
      cellStyle: { height: '50px' },
      // rightAlignContent: true,
      defaultSortDirection: SortDirection.DESC,
      cellContent: ({ monthly_dict }) => {
        const keys = R.keys(monthly_dict);
        const values = R.values(monthly_dict);
        const trendData = values.map((k, i) => {
          return {
            name: keys[i],
            value: k && k.value ? k.value : k,
          };
        });

        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart width={150} height={50} data={trendData}>
              <Line
                type="monotone"
                dataKey="value"
                stroke="#00a1af"
                strokeWidth={1}
                dot={{ fill: '#00a1af', strokeWidth: 0, r: 2 }}
                isAnimationActive={false}
              />
              <Tooltip
                formatter={(value, name, props) => {
                  if (value && typeof value === 'number') {
                    return [`${formatNumber(value, 1)} kg`];
                  }
                  return [];
                }}
                labelFormatter={(value, name, props) => {
                  return MONTH_SHORTEN[value];
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      },
      skeletonCellContent: (rowIndex: OpacityIndex) => <ShortSkeletonCell />,
    },
    {
      header: 'Jan',
      width: '3%',
      align: 'right',
      rightAlignContent: true,
      defaultSortDirection: SortDirection.DESC,
      cellContent: ({ monthly_dict }) => {
        const data = R.values(monthly_dict)[0];
        const { value, status } =
          data instanceof Object
            ? data
            : {
                value: data,
                status: true,
              };
        return (
          <div className={status ? '' : styles.nonComplete}>
            {formatNumber(value || 0, 1)}
          </div>
        );
      },
      skeletonCellContent: (rowIndex: OpacityIndex) => <ShortSkeletonCell />,
    },
    {
      header: 'Feb',
      width: '5%',
      align: 'right',
      rightAlignContent: true,
      defaultSortDirection: SortDirection.DESC,
      cellContent: ({ monthly_dict }) => {
        const data = R.values(monthly_dict)[1];
        const { value, status } =
          data instanceof Object
            ? data
            : {
                value: data,
                status: true,
              };
        return (
          <div className={status ? '' : styles.nonComplete}>
            {formatNumber(value || 0, 1)}
          </div>
        );
      },
      skeletonCellContent: (rowIndex: OpacityIndex) => <ShortSkeletonCell />,
    },
    {
      header: 'Mar',
      width: '5%',
      align: 'right',
      rightAlignContent: true,
      defaultSortDirection: SortDirection.DESC,
      cellContent: ({ monthly_dict }) => {
        const data = R.values(monthly_dict)[2];
        const { value, status } =
          data instanceof Object
            ? data
            : {
                value: data,
                status: true,
              };
        return (
          <div className={status ? '' : styles.nonComplete}>
            {formatNumber(value || 0, 1)}
          </div>
        );
      },
      skeletonCellContent: (rowIndex: OpacityIndex) => <ShortSkeletonCell />,
    },
    {
      header: 'Apr',
      width: '5%',
      align: 'right',
      rightAlignContent: true,
      defaultSortDirection: SortDirection.DESC,
      cellContent: ({ monthly_dict }) => {
        const data = R.values(monthly_dict)[3];
        const { value, status } =
          data instanceof Object
            ? data
            : {
                value: data,
                status: true,
              };
        return (
          <div className={status ? '' : styles.nonComplete}>
            {formatNumber(value || 0, 1)}
          </div>
        );
      },
      skeletonCellContent: (rowIndex: OpacityIndex) => <ShortSkeletonCell />,
    },
    {
      header: 'May',
      width: '5%',
      align: 'right',
      rightAlignContent: true,
      defaultSortDirection: SortDirection.DESC,
      cellContent: ({ monthly_dict }) => {
        const data = R.values(monthly_dict)[4];
        const { value, status } =
          data instanceof Object
            ? data
            : {
                value: data,
                status: true,
              };
        return (
          <div className={status ? '' : styles.nonComplete}>
            {formatNumber(value || 0, 1)}
          </div>
        );
      },
      skeletonCellContent: (rowIndex: OpacityIndex) => <ShortSkeletonCell />,
    },
    {
      header: 'Jun',
      width: '5%',
      align: 'right',
      rightAlignContent: true,
      defaultSortDirection: SortDirection.DESC,
      cellContent: ({ monthly_dict }) => {
        const data = R.values(monthly_dict)[5];
        const { value, status } =
          data instanceof Object
            ? data
            : {
                value: data,
                status: true,
              };
        return (
          <div className={status ? '' : styles.nonComplete}>
            {formatNumber(value || 0, 1)}
          </div>
        );
      },
      skeletonCellContent: (rowIndex: OpacityIndex) => <ShortSkeletonCell />,
    },
    {
      header: 'Jul',
      width: '5%',
      align: 'right',
      rightAlignContent: true,
      defaultSortDirection: SortDirection.DESC,
      cellContent: ({ monthly_dict }) => {
        const data = R.values(monthly_dict)[6];
        const { value, status } =
          data instanceof Object
            ? data
            : {
                value: data,
                status: true,
              };
        return (
          <div className={status ? '' : styles.nonComplete}>
            {formatNumber(value || 0, 1)}
          </div>
        );
      },
      skeletonCellContent: (rowIndex: OpacityIndex) => <ShortSkeletonCell />,
    },
    {
      header: 'Aug',
      width: '5%',
      align: 'right',
      rightAlignContent: true,
      defaultSortDirection: SortDirection.DESC,
      cellContent: ({ monthly_dict }) => {
        const data = R.values(monthly_dict)[7];
        const { value, status } =
          data instanceof Object
            ? data
            : {
                value: data,
                status: true,
              };
        return (
          <div className={status ? '' : styles.nonComplete}>
            {formatNumber(value || 0, 1)}
          </div>
        );
      },
      skeletonCellContent: (rowIndex: OpacityIndex) => <ShortSkeletonCell />,
    },
    {
      header: 'Sep',
      width: '5%',
      align: 'right',
      rightAlignContent: true,
      defaultSortDirection: SortDirection.DESC,
      cellContent: ({ monthly_dict }) => {
        const data = R.values(monthly_dict)[8];
        const { value, status } =
          data instanceof Object
            ? data
            : {
                value: data,
                status: true,
              };
        return (
          <div className={status ? '' : styles.nonComplete}>
            {formatNumber(value || 0, 1)}
          </div>
        );
      },
      skeletonCellContent: (rowIndex: OpacityIndex) => <ShortSkeletonCell />,
    },
    {
      header: 'Oct',
      width: '5%',
      align: 'right',
      rightAlignContent: true,
      defaultSortDirection: SortDirection.DESC,
      cellContent: ({ monthly_dict }) => {
        const data = R.values(monthly_dict)[9];
        const { value, status } =
          data instanceof Object
            ? data
            : {
                value: data,
                status: true,
              };
        return (
          <div className={status ? '' : styles.nonComplete}>
            {formatNumber(value || 0, 1)}
          </div>
        );
      },
      skeletonCellContent: (rowIndex: OpacityIndex) => <ShortSkeletonCell />,
    },
    {
      header: 'Nov',
      width: '5%',
      align: 'right',
      rightAlignContent: true,
      defaultSortDirection: SortDirection.DESC,
      cellContent: ({ monthly_dict }) => {
        const data = R.values(monthly_dict)[10];
        const { value, status } =
          data instanceof Object
            ? data
            : {
                value: data,
                status: true,
              };
        return (
          <div className={status ? '' : styles.nonComplete}>
            {formatNumber(value || 0, 1)}
          </div>
        );
      },
      skeletonCellContent: (rowIndex: OpacityIndex) => <ShortSkeletonCell />,
    },
    {
      header: 'Dec',
      width: '5%',
      align: 'right',
      rightAlignContent: true,
      defaultSortDirection: SortDirection.DESC,
      cellContent: ({ monthly_dict }) => {
        const data = R.values(monthly_dict)[11];
        const { value, status } =
          data instanceof Object
            ? data
            : {
                value: data,
                status: true,
              };
        return (
          <div className={status ? '' : styles.nonComplete}>
            {formatNumber(value || 0, 1)}
          </div>
        );
      },
      skeletonCellContent: (rowIndex: OpacityIndex) => <ShortSkeletonCell />,
    },
  ];

  const processData = (siteData) => {
    return R.compose(
      R.values,
      R.mapObjIndexed((item, key, obj) => {
        return {
          siteId: key,
          ...item,
        };
      })
    )(siteData);
  };

  const data = mapValues(processData(site_dict), (item) => item);

  const groupHeaders: TableGroupHeader[] = [
    {
      header: '',
      colSpan: 3,
    },
    {
      header: 'CO₂ Emissions (kg)',
      colSpan: 12,
    },
  ];

  const {
    query,
    setQuery,
    filteredList: filterSearchList,
  } = useSearchFilter(data, [['site_title']]);

  const displayData = filterSearchList.filter((site) => {
    if (selectedSiteFilters.length > 0) {
      return selectedSiteFilters.includes(Number(site.siteId));
    } else {
      return true;
    }
  });

  const { currentPage, rowLimit, setPagination } = usePaginationFilter(
    data?.length
  );

  return (
    <SeeAllPage
      title="Summary"
      displayCountText="sites"
      headerName="Annual Performance"
      backRoute={Routes.SUMMARY}
      searchProps={{
        query,
        onChange: setQuery,
        error: filterSearchList.length === 0,
      }}
      tabs={siteFilter}
      filters={tableLinks}
      customDateFilter={
        <DateFilter
          value={selectedFilterOption}
          selectedYear={selectedYear}
          setSelectedYear={updateYearParam}
          setFilter={updateSelectedFilterOption}
          optionCreator={() => []}
          disableCustomRange={true}
          disableBefore={DA_REPORT_AVAILABLE_DATE}
          disableAfter={disableAfterOption}
          displayDatesAsTitle
          customPickerVariant="yearly"
          alignItemsRight={true}
          locale={locale}
        />
      }
      summaryStatistics={[
        {
          value: isNumber(org_co2_total)
            ? formatNumber(org_co2_total, 1) + ' kg'
            : '-',
          title: 'CO₂ Emissions',
          bold: true,
        },
      ]}
      tableProps={{
        groupHeaders,
        columns,
        data: displayData,
        dataIsLoading: isLoading,
        listView: true,
        rowLimitFromPaginationHook: rowLimit,
        currentPageFromPaginationHook: currentPage,
        setPagination: setPagination,
      }}
    ></SeeAllPage>
  );
};

const tableLinks = [
  <Link to={Routes.ANNUAL_ENERGY_CONSUMPTION}>
    <Button
      size="small"
      secondary
      style={{ border: '1px solid', borderRadius: '5px' }}
    >
      {'Energy Consumption'}
    </Button>
  </Link>,

  <Link to={Routes.ANNUAL_USAGE_INTENSITY}>
    <Button
      size="small"
      secondary
      style={{ border: '1px solid', borderRadius: '5px' }}
    >
      {'EUI'}
    </Button>
  </Link>,

  <Link to={Routes.ANNUAL_CO2_EMISSION}>
    <Button size="small" style={{ border: '1px solid', borderRadius: '5px' }}>
      {'CO₂ Emissions'}
    </Button>
  </Link>,
];

const siteFilter = [<SiteFilter />];

export default AnnualCO2Emission;
